import React, { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { useReactToPrint } from 'react-to-print';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import { Grid, IconButton, Tooltip } from '@material-ui/core';
import Card from '../Card/Card.js';
import CardBody from '../Card/CardBody.js';
import ContactScript from './ContactScript';
import { makeStyles } from '@material-ui/core/styles';
import HomeIcon from '@material-ui/icons/Home';
import ProactiveContactIcon from '../../assets/img/proactive-contact-icon.svg';
import { grayColor, secondaryColor, webPrimaryColor, whiteColor } from '../../assets/jss/nextjs-material-kit-pro.js';
import useAuthRedirect from '../../hooks/authRedirectHook';
import { isAdmin } from '../../util/miscUtil.js';

const useStyles = makeStyles((theme) => ({
  welcome: {
    margin: 25,
  },
  menu: {
    display: 'flex',
    justifyContent: 'center',
    padding: '0 30px',
  },
  menuTitle: {
    fontSize: 24,
    textAlign: 'left',
    margin: '5px 0',
    padding: 10,
  },
  appSubmittedDate: {
    fontSize: 24,
    margin: '5px 0 50px 0',
    padding: 10,
  },
  appStatus: {
    fontSize: 20,
    position: 'relative',
    bottom: '5px',
  },
  statusIcon: {
    fontSize: 20,
  },
  appStatusDate: {
    fontSize: 20,
    position: 'relative',
    bottom: '5px',
    paddingLeft: 25,
  },
  statusBottom: {
    marginLeft: 10,
    position: 'absolute',
    bottom: '20px',
  },
  icon: {
    fontSize: '100px',
  },
  nav: {
    cursor: 'pointer',
  },
  homeIconSpace: {
    marginTop: 50,
  },
  card: {
    [theme.breakpoints.down('sm')]: {
      height: '280px',
      minHeight: '280px',
    },
    [theme.breakpoints.up('md')]: {
      height: '375px',
      minHeight: '375px',
    },
    margin: '0, 20px',
  },
  pageHeader: {
    margin: '10px 0 0 0',
  },
  pageTitle: {
    fontSize: '1.438rem',
    textAlign: 'left',
    fontWeight: 600,
  },
  pageIcon: {
    width: '8%',
    marginRight: '15px',
  },
  printHeaderText: {
    color: '#000',
    fontWeight: 600,
  },
  homeIcon: {
    fontSize: '30px',
  },
  pageHeading: {
    display: 'flex',
    justifyContent: 'center',
    padding: 30,
  },
  pageDesc: {
    fontSize: 14,
    fontWeight: 500,
    textAlign: 'left',
    padding: '10px 5px 15px 5px',
    color: '#000',
  },
  breadcrumb: {
    padding: '18px 0 0 10px',
  },
  addIcon: {
    color: whiteColor,
    fontSize: '80px',
    marginTop: 40,
    textAlign: 'center',
  },
  addSection: {
    display: 'flex',
    justifyContent: 'center',
  },
  headerCard: {
    padding: '20px',
  },
  headerDesc: {
    width: '90%',
    fontSize: '1.063rem',
    lineHeight: '1.313rem',
    padding: '20px 0 5px 0',
    textAlign: 'left',
    color: webPrimaryColor,
  },
  printHeaderDesc: {
    width: '90%',
    fontSize: 14,
    fontWeight: 500,
    textAlign: 'left',
    padding: '10px 0',
    color: webPrimaryColor,
  },
  breadcrumbs: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      minWidth: '95%',
      padding: '18px 0 20px 5px',
    },
    [theme.breakpoints.up('md')]: {
      minWidth: '95vw',
      padding: '18px 0 0 10px',
    },
  },
  breadCrumbLink: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    fontSize: '0.80rem',
    cursor: 'pointer',
    '&:hover,&:focus': {
      textDecoration: 'underline',
    },
    color: grayColor,
  },
  breadCrumbText: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    color: secondaryColor,
    fontSize: '0.80rem',
  },
}));

const convertType = (type) => {
  if (type === '3m') {
    return '3 months';
  }
  if (type === '12m') {
    return '12 months';
  }
  if (type === '9m') {
    return '9 months';
  }
  if (type === '6m') {
    return '6 months';
  }
  return type;
};

const Contact = inject(
  'SeniorStore',
  'AuthStore'
)(
  observer((props) => {
    const store = props.SeniorStore;
    const auth = props.AuthStore;
    const classes = useStyles();
    const history = useHistory();

    const [showHelp, setShowHelp] = useState(false);

    const componentRef = useRef();

    const handlePrint = useReactToPrint({
      content: () => componentRef.current,
    });

    const handleHelpClose = () => {
      setShowHelp(false);
    };

    const handleHelp = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setShowHelp(true);
    };

    const handleReturnHome = () => {
      store.setScholarshipAppId(null);
      store.setScholarshipApp(null);
      store.setSenior(null);
      history.push('/');
    };

    const handleReturnToContactList = () => {
      store.setHelpAppId(null);
      store.setHelpApp(null);
      store.setCheckInId(null);
      // store.setSenior(null);
      history.push('/contactlistmainMenu');
    };

    // .
    useAuthRedirect(auth);

    return (
      <React.Fragment>
        {isAdmin(auth.roles) && (
          <Grid container direction="row">
            <Grid item xs={12} style={{ margin: '10px 5px' }}>
              <div className={classes.breadcrumbs} style={{ margin: '10px 5px' }}>
                <Breadcrumbs separator="›" aria-label="breadcrumbs" className={classes.alignCrumb}>
                  <span
                    className={classes.breadCrumbLink}
                    style={{ marginTop: '-1px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    onClick={(e) => history.push('/')}
                  >
                    <HomeIcon style={{ fontSize: '1.30rem' }} />
                    <span style={{ margin: '1px 0 0 1px' }}>Home</span>
                  </span>
                  {store.checkInId > 0 && (
                    <span className={classes.breadCrumbLink} style={{ marginTop: '-1px' }} onClick={(event) => history.push('/alumnilist')}>
                      Alumni
                    </span>
                  )}
                  <span className={classes.breadCrumbLink} style={{ marginTop: '-1px' }} onClick={(e) => history.push('/seniorplanner')}>
                    {store.senior && store.senior.alumni_updated_first_name ? store.senior.alumni_updated_first_name : store?.senior?.user.first_name}{' '}
                    {store.senior && store.senior.alumni_updated_last_name ? store.senior.alumni_updated_last_name : store?.senior?.user.last_name}
                  </span>
                  <span className={classes.breadCrumbLink} style={{ marginTop: '-1px' }} onClick={(e) => handleReturnToContactList()}>
                    Proactive Check-Ins
                  </span>
                  <span className={classes.breadCrumbText} style={{ marginTop: '-1px' }}>
                    {convertType(store.checkIn.type)} Check-In
                  </span>
                </Breadcrumbs>
              </div>
            </Grid>
          </Grid>
        )}
        <Grid container spacing={2} direction="row" className={classes.menu} ref={componentRef}>
          <Grid item xs={12} align="left">
            <Card className={classes.pageHeader}>
              <CardBody className={classes.headerCard}>
                <Grid container direction="row">
                  <Grid item xs={11} align="left">
                    <section className={classes.pageTitle}>
                      <img src={ProactiveContactIcon} alt="Proactive Check-In" className={classes.pageIcon} />
                      <span className={classes.pagecardHeader}>Proactive Check-Ins</span>
                    </section>
                  </Grid>
                  <Grid item xs={1} align="right">
                    <Tooltip title="Click to return to the planner home page." placement="bottom">
                      <IconButton color="inherit" aria-label="return home" onClick={(e) => history.push('/contactlist')}>
                        <HomeIcon className={classes.homeIcon} />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </CardBody>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <ContactScript SeniorStore={store} AuthStore={auth} handleSaveReturn={handleReturnToContactList} convertType={convertType} />
          </Grid>
        </Grid>
      </React.Fragment>
    );
  })
);

export default Contact;
